import { RefObject, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

import { ERoutes } from "@utils/constants";

/**
 * Reserve a global banner's height at the top of the page — or deliberately
 * don't, on the pages where reserving it would be counted twice.
 *
 * Every global banner renders inside the fixed `SecondHeaderContainer`. A fixed
 * element is out of normal flow, so nothing below it knows it is there: without
 * help, the page's first content slides up underneath the banner. Each banner
 * therefore publishes its measured height into its own CSS variable, and
 * `body`'s `padding-top` in `index.css` adds them all up.
 *
 * The exception is the whole point of this hook. On `/account/*` — every page
 * except `/account/claims` — the header is NOT fixed (`disableFade` in
 * `SecondHeader` switches it to `position: static`), so it flows normally and
 * the banners inside it already take up real space. Adding the body padding on
 * top of that reserves the same strip twice, and the second copy shows up as an
 * empty band above the header. With one banner it is easy to miss; with several
 * showing at once it is a hole at the top of the page.
 *
 * This lived in two of the nine banners and was missing from the other seven,
 * which is why it kept coming back. It is one rule, so it lives in one place.
 *
 * Measured with a ResizeObserver rather than read once: banner copy wraps to a
 * second line at narrow widths, and a stale height is the same bug again.
 *
 * `useLayoutEffect`, not `useEffect`, so the variable is set before the browser
 * paints — otherwise the first frame lays out against `0px` and the content
 * visibly jumps.
 */
export const useBannerOffset = (
  cssVar: string,
  visible: boolean,
  ref: RefObject<HTMLElement | null>,
): void => {
  const { pathname } = useLocation();
  const headerInFlow = pathname.startsWith("/account") && !pathname.startsWith(ERoutes.AccountVideos);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!visible || !el || headerInFlow) {
      root.style.setProperty(cssVar, "0px");
      return;
    }
    const measure = () => root.style.setProperty(cssVar, `${el.getBoundingClientRect().height}px`);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => {
      ro.disconnect();
      root.style.setProperty(cssVar, "0px");
    };
  }, [cssVar, visible, headerInFlow, ref]);
};
